/**
 * Cross-UI session bridge.
 *
 * During the legacy → fullwidget migration both frontends are served
 * from the same subdomain and the user can toggle between them
 * (?ui=legacy|new). To keep a logged-in session across that toggle we
 * mirror the session into a neutral localStorage blob that both apps
 * read on boot:
 *
 *   activiter_session = { token: "<jwt>", user: { id, email, role, ... } }
 *
 * Each app already holds the full user object at login time, so this is
 * a plain copy — no extra network call, no shape juggling. The legacy
 * reducer writes/clears it; the new widget's auth storage adapter does
 * the same on its side. On boot, whichever app has no native session
 * adopts the shared blob.
 *
 * Known limitation: logout fully syncs only when the shared blob is
 * cleared (done here) and the other app hasn't cached its own native
 * session. Good enough for the migration window.
 */

const SHARED_KEY = 'activiter_session';

export function readSharedSession() {
    try {
        const raw = localStorage.getItem(SHARED_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (parsed && parsed.token && parsed.user) {
            return parsed;
        }
        return null;
    } catch (e) {
        return null;
    }
}

export function writeSharedSession(token, user) {
    try {
        if (!token || !user) {
            return;
        }
        localStorage.setItem(SHARED_KEY, JSON.stringify({ token, user }));
    } catch (e) {
        /* quota / private mode — ignore */
    }
}

export function clearSharedSession() {
    try {
        localStorage.removeItem(SHARED_KEY);
    } catch (e) {
        /* ignore */
    }
}
